import type React from "react";
import { useEffect, useState } from "react";
import { Modal } from "~/components/Modal";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import * as Analytics from "../../analytics";
import type { ScreenerAsset } from "../../types";
import { validateEmail } from "~/utils/validators/validateEmail";

// // // //

export type DataRoomAccessMode = "data-room" | "factsheet";

export function DataRoomAccessModal(props: {
    asset: ScreenerAsset;
    show: boolean;
    onHide: () => void;
    mode: DataRoomAccessMode;
    documentOverride?: ScreenerAsset["dataRoomDocuments"][number] | null;
}) {
    const { asset, show, onHide, mode, documentOverride = null } = props;
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const nameIsValid = name.trim().length > 0;
    const emailIsValid = validateEmail(email);
    const document = documentOverride ?? asset.dataRoomDocuments[0] ?? null;
    const isFactsheetAccess = mode === "factsheet";
    const modalTitle = isFactsheetAccess
        ? "Access 1-pager factsheet"
        : "Access data room";
    const modalBody = isFactsheetAccess
        ? `Enter your details to open the available 1-pager factsheet for ${asset.name}. This prototype validates the fields locally and does not store your information.`
        : `Enter your details to open the available documents for ${asset.name}. This prototype validates the fields locally and does not store your information.`;
    const submitLabel = isFactsheetAccess ? "Open factsheet" : "Open documents";

    useEffect(() => {
        if (!show) {
            setName("");
            setEmail("");
            setHasSubmitted(false);
        }
    }, [show]);

    return (
        <Modal show={show} onHide={onHide} allowScroll={false}>
            <form
                className="w-full rounded-lg border bg-card p-5 shadow-xl"
                onSubmit={(event) => {
                    event.preventDefault();
                    setHasSubmitted(true);

                    if (!nameIsValid || !emailIsValid || !document) {
                        return;
                    }

                    Analytics.trackAssetCardSubmitDataRoomAccess({
                        asset_id: asset.id,
                        asset_ticker: asset.symbol,
                        document_name: document.name,
                        access_type: mode,
                    });
                    window.open(document.url, "_blank", "noopener,noreferrer");
                    onHide();
                }}
            >
                <div>
                    <p className="text-base font-semibold tracking-tight text-foreground">
                        {modalTitle}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-secondary-foreground">
                        {modalBody}
                    </p>
                </div>

                <div className="mt-5 space-y-4">
                    <div className="space-y-1.5">
                        <label
                            htmlFor={`data-room-name-${asset.id}`}
                            className="text-sm font-medium text-secondary-foreground"
                        >
                            Name
                        </label>
                        <Input
                            id={`data-room-name-${asset.id}`}
                            value={name}
                            autoComplete="name"
                            onChange={(event) => setName(event.target.value)}
                            className="h-9"
                        />
                        {hasSubmitted && !nameIsValid && (
                            <p className="text-xs text-red-600">
                                Enter your name.
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <label
                            htmlFor={`data-room-email-${asset.id}`}
                            className="text-sm font-medium text-secondary-foreground"
                        >
                            Email
                        </label>
                        <Input
                            id={`data-room-email-${asset.id}`}
                            type="email"
                            value={email}
                            autoComplete="email"
                            onChange={(event) => setEmail(event.target.value)}
                            className="h-9"
                        />
                        {hasSubmitted && !emailIsValid && (
                            <p className="text-xs text-red-600">
                                Enter a valid email address.
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9"
                        onClick={onHide}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        className="h-9"
                        disabled={!document}
                    >
                        {submitLabel}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
